import * as React from 'react'
import debounce from 'lodash.debounce'
import { SelectOption, FilterFetchOptions } from '../FilterGroup.types'
import { Spin } from 'antd-v4'
import { SelectProps, SelectValue } from 'antd-v4/lib/select'

export interface DebounceFetchProps<T extends SelectValue> extends SelectProps<T> {
  fetchOptions: FilterFetchOptions
  debounceTimeout?: number
  value?: T
  onChange?: (arg?: unknown) => void
  fetchOnMount?: boolean
  bordered?: boolean
  render: (props: SelectProps<T>) => React.ReactElement
}

/**
 * Debounce Fetch
 *
 * Render prop pattern used here to fetch and pass options to a normal
 * ant-design select field. Using the recommended method from Ant-Design to
 * handle fetch logic
 *
 * @link https://ant.design/components/select/#components-select-demo-select-users
 * @todo maybe turn this into an even more general purpose hoc and move to a shared
 */
const DebounceFetch = <T extends SelectValue>(props: DebounceFetchProps<T>): React.ReactElement => {
  const { fetchOptions, debounceTimeout = 800, fetchOnMount, render } = props

  const [searchValue, setSearchValue] = React.useState('')
  const [fetching, setFetching] = React.useState(false)
  const [selectOptions, setSelectOptions] = React.useState<SelectOption[]>([])
  const fetchRef = React.useRef(0)

  React.useEffect(() => {
    if (fetchOnMount) fetchLogic('')
  }, [fetchOnMount])

  const onSearch = (val: string) => {
    setSearchValue(val)
    debounceFetcher(val)
  }
  const debounceFetcher = React.useMemo(() => {
    const loadOptions = (value: string) => {
      fetchLogic(value)
      setSearchValue(value)
    }
    return debounce(loadOptions, debounceTimeout)
  }, [fetchOptions, debounceTimeout])

  const fetchLogic = (value: string) => {
    fetchRef.current += 1
    const fetchId = fetchRef.current
    setSelectOptions([])
    setFetching(true)

    fetchOptions(value).then((newOptions: SelectOption[]) => {
      if (fetchId !== fetchRef.current) {
        // for fetch callback order
        return
      }
      setSelectOptions(newOptions)
      setFetching(false)
    })
  }

  const noResults = (): JSX.Element | string => {
    const hasSearched = Boolean(searchValue && searchValue.length)
    const noOptions = Boolean(selectOptions && !selectOptions.length)
    return fetching ? (
      <div style={{ textAlign: 'center' }}>
        <Spin size='small' />
      </div>
    ) : hasSearched && noOptions ? (
      'No results found'
    ) : (
      'Type to search'
    )
  }

  const newProps: SelectProps<T> = {
    filterOption: false,
    onSearch,
    options: selectOptions,
    loading: fetching,
    notFoundContent: noResults(),
  }
  return render(newProps)
}
export default DebounceFetch
